/**
 * Choisir les élèves d'un nouveau message : recherche par nom, puis rappel des
 * élèves retenus. La création elle-même est dans NewMessageForm.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Loader2, Search, X } from 'lucide-react';
import {
  MIN_STUDENT_SEARCH_LENGTH,
  studentMessagingApi,
  studentMessagingError,
  studentMessagingKeys,
  type StudentContact,
} from '../api/studentMessaging';
import { useAuthStore } from '../store/auth';
import { OFFLINE, initials } from './StudentMessageParts';
import { StudentPhoto } from './Photos';

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Recherche d'un élève de l'école par son nom, à partir de deux caractères. */
function StudentSearch({
  label,
  picked,
  onPick,
}: {
  label: string;
  /** Élèves déjà retenus : ils restent affichés, marqués « Ajouté ». */
  picked: string[];
  onPick: (student: StudentContact) => void;
}) {
  const userId = useAuthStore((s) => s.user?.id);
  const [value, setValue] = useState('');
  const typed = value.trim();
  const term = useDebounced(typed, 300);
  const ready =
    typed.length >= MIN_STUDENT_SEARCH_LENGTH &&
    term.length >= MIN_STUDENT_SEARCH_LENGTH;
  const results = useQuery({
    queryKey: studentMessagingKeys.students(userId, term),
    queryFn: () => studentMessagingApi.searchStudents(term),
    enabled: term.length >= MIN_STUDENT_SEARCH_LENGTH,
    staleTime: 0,
    retry: false,
    gcTime: 0,
  });
  const settled = ready && typed === term && !results.isFetching;

  return (
    <div>
      <label>
        {label}
        <span className="messaging-inbox-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="text"
            enterKeyHint="search"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            // Entrée ne valide pas le formulaire qui contient la recherche.
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            placeholder="Nom de l’élève"
            autoComplete="off"
            maxLength={120}
          />
          {ready && results.isFetching && (
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          )}
        </span>
      </label>
      {typed.length < MIN_STUDENT_SEARCH_LENGTH && (
        <p className="messaging-hint mt-1.5">
          Saisissez au moins {MIN_STUDENT_SEARCH_LENGTH} caractères du nom.
        </p>
      )}
      {settled && results.isError && (
        <p role="alert" className="messaging-alert mt-2">
          {studentMessagingError(results.error, OFFLINE)}
        </p>
      )}
      {settled && !results.isError && results.data?.length === 0 && (
        <p className="messaging-hint mt-2">Aucun élève trouvé.</p>
      )}
      {ready && !results.isError && !!results.data?.length && (
        <ul className="messaging-results">
          {results.data.map((student) => {
            const added = picked.includes(student.id);
            return (
              <li key={student.id}>
                <button
                  type="button"
                  disabled={added}
                  onClick={() => onPick(student)}
                >
                  <span
                    aria-hidden="true"
                    className="messaging-avatar messaging-avatar--student"
                  >
                    {initials(student.name)}
                    <StudentPhoto candidateId={student.id} />
                  </span>
                  <span className="flex-1 min-w-0 truncate">
                    {student.name}
                  </span>
                  {added && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 flex-shrink-0">
                      <Check size={14} aria-hidden="true" />
                      Ajouté
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Choix des élèves d'un nouveau message : recherche par nom, puis rappel des
 * élèves retenus, chacun pouvant être retiré.
 */
export function StudentPicker({
  students,
  onChange,
}: {
  students: StudentContact[];
  onChange: (students: StudentContact[]) => void;
}) {
  return (
    <div className="space-y-2">
      <StudentSearch
        label="Ajouter des élèves"
        picked={students.map((s) => s.id)}
        onPick={(student) => {
          if (!students.some((s) => s.id === student.id))
            onChange([...students, student]);
        }}
      />
      {students.length > 0 && (
        <ul aria-label="Élèves choisis" className="messaging-student-chips">
          {students.map((student) => (
            <li key={student.id}>
              {student.name}
              <button
                type="button"
                aria-label={`Retirer ${student.name}`}
                onClick={() =>
                  onChange(students.filter((s) => s.id !== student.id))
                }
              >
                <X size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
